import { motion, useReducedMotion } from "framer-motion";
import TopoCanvas from "./TopoCanvas";
import { ShimmerButton } from "@/components/ui/shimmer-button";

const EASE = [0.16, 1, 0.3, 1];

const HERO_STATS = [
  { label: "Founded", value: "2024", sub: "Peyton, CO" },
  { label: "Status", value: "Veteran-Owned", sub: "Army · Solo-run" },
  { label: "Capacity", value: "1 Build / Month", sub: "No queue" },
  { label: "Home Base", value: "Colorado", sub: "6,800 FT" },
  ];

function scrollToId(id) {
    const el = document.getElementById(id);
    if (!el) return;
    if (window.__lenis) window.__lenis.scrollTo(el, { offset: -80, duration: 1.2 });
    else el.scrollIntoView({ behavior: "smooth" });
}

const MaskLine = ({ children, delay }) => {
    const reduce = useReducedMotion();
    return (
          <span className="line-mask">
                <motion.span
                          className="block"
                          initial={reduce ? { opacity: 0 } : { y: "110%" }}
                          animate={reduce ? { opacity: 1 } : { y: "0%" }}
                          transition={{ duration: 1.05, ease: EASE, delay }}
                        >
                  {children}
                </motion.span>
          </span>
        );
};

export default function Hero() {
    return (
          <section id="top" className="relative z-10 overflow-hidden bg-paper">
            {/* live, cursor-reactive procedural contour canvas (shared TopoCanvas, real JS-rendered terrain) */}
                <TopoCanvas variant="light" />
            {/* soft vignette + bottom fade into paper */}
                <div
                          aria-hidden="true"
                          className="pointer-events-none absolute inset-0 z-[1]"
                          style={{
                                      background:
                                                    "radial-gradient(120% 90% at 20% 12%, rgba(247,244,238,0) 34%, rgba(247,244,238,0.75) 100%)",
                          }}
                        />
          
                <div className="relative z-10 px-5 pt-36 md:px-8 md:pt-44">
                        <div className="mx-auto max-w-7xl">
                                  <motion.div
                                                initial={{ opacity: 0, y: 12 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ duration: 0.8, ease: EASE, delay: 0.4 }}
                                                className="inline-flex items-center gap-2 rounded-full border border-hairline bg-ink/5 px-4 py-2 backdrop-blur-sm"
                                                data-testid="hero-elevation-badge"
                                              >
                                              <span className="h-1.5 w-1.5 rounded-full bg-rust" />
                                              <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-ink">
                                                            Peyton, CO · 6,800 FT
                                              </span>
                                  </motion.div>
                        
                                  <h1
                                                data-testid="hero-headline"
                                                className="mt-8 font-serif text-[3.25rem] font-medium leading-[0.95] tracking-tight text-ink sm:text-7xl md:text-8xl lg:text-[8.5rem]"
                                              >
                                              <MaskLine delay={0.55}>Websites built</MaskLine>
                                              <MaskLine delay={0.68}>
                                                            <em className="italic text-pine">at elevation,</em>
                                              </MaskLine>
                                              <MaskLine delay={0.81}>with precision.</MaskLine>
                                  </h1>
                        
                                  <motion.p
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 1 }}
                                                transition={{ duration: 1, delay: 1.1 }}
                                                className="mt-8 max-w-xl text-base leading-relaxed text-ink/85 md:text-lg"
                                                data-testid="hero-subhead"
                                              >
                                              Veteran-owned, one-person shop. Every site is scoped like a mission,
                                              mapped like terrain, and delivered without surprises.
                                  </motion.p>
                        
                                  <motion.div
                                                initial={{ opacity: 0, y: 14 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ duration: 0.9, ease: EASE, delay: 1.25 }}
                                                className="mt-9 flex flex-wrap items-center gap-4"
                                              >
                                              <ShimmerButton
                                                              data-testid="hero-start-build"
                                                              onClick={() => scrollToId("contact")}
                                                              background="#2F4A3E"
                                                              shimmerColor="#FBF6EE"
                                                              className="px-7 py-3.5 font-mono text-xs uppercase tracking-[0.16em] transition-transform duration-300 hover:scale-[1.03] active:scale-95"
                                                            >
                                                            Start a build
                                              </ShimmerButton>
                                              <button
                                                              data-testid="hero-why"
                                                              onClick={() => scrollToId("about")}
                                                              className="group inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.16em] text-ink"
                                                            >
                                                            Why Zigler Designs
                                                            <span className="transition-transform duration-300 group-hover:translate-x-1">
                                                                            →
                                                            </span>
                                              </button>
                                  </motion.div>
                        
                                  <motion.div
                                                initial={{ opacity: 0, y: 20 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ duration: 1, ease: EASE, delay: 1.4 }}
                                                className="mt-16 grid translate-y-16 grid-cols-2 rounded-lg border border-hairline bg-panel/70 backdrop-blur-md shadow-[0_30px_70px_-30px_rgba(0,0,0,0.8)] md:mt-24 md:grid-cols-4"
                                                data-testid="hero-stat-strip"
                                              >
                                    {HERO_STATS.map((s, i) => (
                                                              <div
                                                                                key={s.label}
                                                                                className={`px-5 py-6 md:px-7 ${
                                                                                                    i !== 0 ? "border-t border-hairline md:border-t-0 md:border-l" : ""
                                                                                } ${i === 1 ? "border-t md:border-t-0" : ""}`}
                                                                              >
                                                                              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink-dim">
                                                                                {s.label}
                                                                              </p>
                                                                              <p className="mt-3 font-serif text-2xl leading-none text-ink md:text-3xl">
                                                                                {s.value}
                                                                              </p>
                                                                              <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.14em] text-ink-dim/80">
                                                                                {s.sub}
                                                                              </p>
                                                              </div>
                                                            ))}
                                  </motion.div>
                        </div>
                        <div className="h-16 md:h-24" />
                </div>
          </section>
        );
}
